/**
 * Formulation 模块消息处理工具函数
 */
import type { AgentMessageResponse, FormulationMessage } from '@/types/formulation/formulation';
import { SEARCH_TYPE_KEYS, AI_THINKING_TYPES } from './types';

/**
 * 判断是否为 AI 思考类型
 */
export const isAiThinkingType = (type: string): boolean => {
  return AI_THINKING_TYPES.has(type);
};

/**
 * 将相邻且 tool_key 相同的 user_message（role===ai）合并为一条展示消息。
 */
export const mergeConsecutiveSameToolKeyMessages = (
  list: FormulationMessage[],
): FormulationMessage[] => {
  if (!list.length) return [];
  const out: FormulationMessage[] = [];
  let i = 0;
  while (i < list.length) {
    const m = list[i];
    if (m.role !== 'ai') {
      out.push(m);
      i += 1;
      continue;
    }
    const key = m.tool_key ?? '';
    const run: FormulationMessage[] = [m];
    let j = i + 1;
    while (
      j < list.length &&
      list[j].role === 'ai' &&
      (list[j].tool_key ?? '') === key &&
      list[j].iteration === m.iteration
    ) {
      run.push(list[j]);
      j += 1;
    }
    if (run.length === 1) {
      out.push(m);
      i += 1;
      continue;
    }
    const mergedMessage = run
      .map((r) => r.message)
      .filter(Boolean)
      .join('\n\n');
    const last = run[run.length - 1];
    out.push({
      id: run[0].id,
      role: 'ai',
      message: mergedMessage,
      iteration: m.iteration,
      status: last.status,
      timestamp: last.timestamp || m.timestamp,
      tool_key: key,
    });
    i = j;
  }
  return out;
};

/**
 * 处理同一轮次、连续的「搜索」类消息：逐条保留，不合并进前一条 AI 思考（intent_ai_thinking、ai_thinking 等），
 * 以便 search 文案只在 search-group 的 displayBlock 中展示；工具消息（search_json_docs、search_summary）单独保留，
 * 便于 SearchGroupBlock 展示卡片并用正确 id 在右侧 DetailSection 展示结果。
 */
export const mergeConsecutiveSearchMessages = (
  list: FormulationMessage[],
): FormulationMessage[] => {
  if (!list.length) return [];
  const out: FormulationMessage[] = [];
  let i = 0;
  while (i < list.length) {
    const m = list[i];
    if (m.role === 'ai' && SEARCH_TYPE_KEYS.has(m.tool_key ?? '')) {
      const run: FormulationMessage[] = [m];
      let j = i + 1;
      while (
        j < list.length &&
        list[j].role === 'ai' &&
        SEARCH_TYPE_KEYS.has(list[j].tool_key ?? '') &&
        list[j].iteration === m.iteration
      ) {
        run.push(list[j]);
        j += 1;
      }
      const lastTimestamp = run[run.length - 1].timestamp;
      // 不再把 search 内容合并进前一条 AI 思考（intent_ai_thinking、ai_thinking 等），避免 search 文案出现在各 AI 思考的 displayBlock 中
      // 逐条输出 search 消息，保留每条自己的 id/tool_key，便于右侧展示 search_json_docs / search_summary
      for (const msg of run) {
        out.push({
          id: msg.id,
          role: 'ai',
          message: msg.message,
          iteration: msg.iteration,
          status: msg.status,
          timestamp: msg.timestamp || lastTimestamp,
          tool_key: msg.tool_key ?? '',
        });
      }
      i = j;
    } else {
      out.push(m);
      i += 1;
    }
  }
  return out;
};

/**
 * 是否已存在相同内容的 user_input，避免重复追加（含 createConversation 跳转后 getConversation 的竞态）
 */
export const hasExistingUserInput = (list: AgentMessageResponse[], rawContent: string): boolean => {
  const contentStr = typeof rawContent === 'string' ? rawContent : '';
  return (list ?? []).some(
    (m) =>
      m.business_type === 'user_input' &&
      typeof m.content?.raw === 'string' &&
      m.content.raw === contentStr,
  );
};
